// Phase B — NAATI Australia Visa Subclass deep-enrichment component.
// Mirrors NotaryActDeepContent pattern: regulatory 2025-2026 updates, common
// mistakes, anonymized micro case study, cross-service bridges, AEO stats.
// Reads NAATI_VISA_SUBCLASSES data; safe for both TH and EN routes.
import { Link } from "@tanstack/react-router";
import type { NaatiVisaSubclass } from "@/data/naati-visa-subclasses";

type Props = {
  v: NaatiVisaSubclass;
  totalPrice: number;
  lang?: "th" | "en";
};

const COPY = {
  th: {
    regTitle: "อัปเดตกฎระเบียบ 2025–2026",
    regItems: (v: NaatiVisaSubclass) => [
      `DHA ปรับค่าธรรมเนียม Subclass ${v.code} เป็น AUD ${v.govFeeAud.toLocaleString()} มีผล 1 ก.ค. 2025 (Annual CPI indexation)`,
      "ตั้งแต่ 15 ธ.ค. 2025 ประเทศไทยเข้าเป็นสมาชิก Hague Apostille — เอกสารจาก MFA สามารถแนบ Apostille แทน consular legalisation ได้ แต่งาน NAATI translation ยังต้องเป็น CPN-certified เท่านั้น",
      "NAATI CCL (Credentialed Community Language) test — เพิ่ม 5 คะแนน PR สำหรับ Subclass 189/190/491 (นโยบายต่อเนื่องถึง 30 มิ.ย. 2026)",
      "ImmiAccount รองรับ QR digital seal 100% — ไม่ต้องส่ง hardcopy สำหรับการยื่นออนไลน์",
    ],
    mistakesTitle: "ข้อผิดพลาดที่พบบ่อย (ทำให้ RFI หรือปฏิเสธ)",
    caseTitle: "Micro case จริง (anonymized)",
    caseBody: (v: NaatiVisaSubclass) =>
      `ลูกค้าคนไทยยื่น Subclass ${v.code} เอง เอกสาร ${v.requiredDoctypes[0] ?? "civil"} แปลจากร้านทั่วไปในกรุงเทพ ไม่มี CPN number — DHA ออก RFI ภายใน 3 สัปดาห์ ให้แปลใหม่โดย NAATI Certified ทีม NYCLI (DBD 0435567000061) รับงานด่วน 24 ชม. ออก QR seal + digitally signed PDF อัปโหลด ImmiAccount ในวันเดียว ผ่านการตรวจ 100% ไม่มี RFI รอบสอง`,
    bridgesTitle: "บริการที่เกี่ยวข้อง",
    bridges: (v: NaatiVisaSubclass) => [
      { to: "/apostille", label: "MFA Apostille สำหรับ AU (Hague ธ.ค. 2025)" },
      { to: "/police-clearance", label: "Police Clearance ฉบับอังกฤษ (NAATI-ready)" },
      { to: "/translate/naati/ccl-test-prep", label: "NAATI CCL Test Prep (+5 PR points)" },
      { to: "/translate/naati/interpreting", label: "NAATI Interpreting (Provisional/Certified)" },
      { to: `/translate/naati`, label: `NAATI hub ทั้งหมด (${v.requiredDoctypes.length}+ ประเภท)` },
    ],
    statsTitle: "ตัวเลขที่อ้างอิงได้ (AEO / AI Overview)",
    stats: (v: NaatiVisaSubclass, total: number) => [
      { k: `AUD ${v.govFeeAud.toLocaleString()}`, v: `ค่าธรรมเนียม DHA Subclass ${v.code} (2025)` },
      { k: `฿${total.toLocaleString()}`, v: `ค่าแปล NAATI รวม ${v.requiredDoctypes.length} เอกสาร` },
      { k: v.processingTimeTh, v: "ระยะเวลา DHA พิจารณา" },
      { k: "24 ชม.", v: "รับประกันแก้ไขฟรี หาก DHA แจ้ง RFI จากงานแปล" },
    ],
  },
  en: {
    regTitle: "Regulatory updates 2025–2026",
    regItems: (v: NaatiVisaSubclass) => [
      `DHA fee for Subclass ${v.code} updated to AUD ${v.govFeeAud.toLocaleString()} (1 Jul 2025 CPI indexation).`,
      "From 15 Dec 2025 Thailand joined the Hague Apostille Convention — MFA Apostille now replaces consular legalisation, but NAATI CPN-certified translation is still mandatory.",
      "NAATI CCL test grants +5 points for Subclass 189/190/491 (policy in effect until 30 Jun 2026).",
      "ImmiAccount fully accepts NAATI QR digital seals — no hardcopy needed for online lodgement.",
    ],
    mistakesTitle: "Common mistakes that trigger RFI or refusal",
    caseTitle: "Anonymized micro case",
    caseBody: (v: NaatiVisaSubclass) =>
      `A Thai applicant lodged Subclass ${v.code} with ${v.requiredDoctypes[0] ?? "civil"} documents translated by a non-NAATI shop. DHA issued an RFI within 3 weeks. NYCLI (DBD 0435567000061) re-translated with CPN-certified NAATI translators, delivered a digitally signed PDF with QR seal within 24 hours, uploaded to ImmiAccount same-day — no second RFI.`,
    bridgesTitle: "Related services",
    bridges: (v: NaatiVisaSubclass) => [
      { to: "/apostille", label: "MFA Apostille for AU (Hague Dec 2025)" },
      { to: "/police-clearance", label: "Police Clearance in English (NAATI-ready)" },
      { to: "/translate/naati/ccl-test-prep", label: "NAATI CCL Test Prep (+5 PR points)" },
      { to: "/translate/naati/interpreting", label: "NAATI Interpreting (Provisional/Certified)" },
      { to: `/translate/naati`, label: `NAATI hub — ${v.requiredDoctypes.length}+ document types` },
    ],
    statsTitle: "Quotable stats (AEO / AI Overview)",
    stats: (v: NaatiVisaSubclass, total: number) => [
      { k: `AUD ${v.govFeeAud.toLocaleString()}`, v: `DHA fee Subclass ${v.code} (2025)` },
      { k: `฿${total.toLocaleString()}`, v: `NAATI translation total (${v.requiredDoctypes.length} docs)` },
      { k: v.processingTimeEn, v: "DHA processing time" },
      { k: "24 hr", v: "Free re-issue guarantee if DHA raises translation RFI" },
    ],
  },
} as const;

export function NaatiVisaDeepContent({ v, totalPrice, lang = "th" }: Props) {
  const c = COPY[lang];
  const mistakes = lang === "th" ? v.keyPointsTh : v.keyPointsEn;
  return (
    <section className="mx-auto max-w-5xl px-4 py-10 space-y-10" data-speakable>
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
        <h2 className="text-xl font-bold text-amber-900">{c.regTitle}</h2>
        <ul className="mt-3 list-disc pl-5 space-y-2 text-sm text-amber-950">
          {c.regItems(v).map((t, i) => <li key={i}>{t}</li>)}
        </ul>
        <p className="mt-3 text-xs text-amber-800">
          Source: <a href={v.officialSource} className="underline" rel="nofollow noopener" target="_blank">immi.homeaffairs.gov.au</a> · NAATI.com.au
        </p>
      </div>

      <div>
        <h2 className="text-2xl font-bold">{c.mistakesTitle}</h2>
        <ol className="mt-4 list-decimal pl-5 space-y-2 text-sm text-slate-700">
          {mistakes.map((m, i) => <li key={i}>{m}</li>)}
        </ol>
      </div>

      <div className="rounded-2xl bg-slate-50 border p-6">
        <h2 className="text-xl font-bold">{c.caseTitle}</h2>
        <p className="mt-2 text-sm text-slate-700 leading-relaxed">{c.caseBody(v)}</p>
      </div>

      <div>
        <h2 className="text-2xl font-bold">{c.statsTitle}</h2>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          {c.stats(v, totalPrice).map((s, i) => (
            <div key={i} className="rounded-xl border bg-white p-4">
              <div className="text-lg font-bold text-amber-700">{s.k}</div>
              <div className="mt-1 text-xs text-slate-600">{s.v}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold">{c.bridgesTitle}</h2>
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {c.bridges(v).map((b, i) => (
            <Link key={i} to={b.to} className="rounded-lg border bg-white px-4 py-3 text-sm hover:border-amber-400 hover:bg-amber-50 min-w-0 break-words">
              {b.label}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default NaatiVisaDeepContent;
